import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import {
  DynamicReviewsExtension,
  CheckoutExtensionUI,
  ProductReviews,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { TypedRuleGroup } from '@app/sharedModule/components/rules-dynamic-form/model';

export { DynamicReviewsExtension };

export const CAROUSEL_DEFAULT_TITLE = DynamicReviewsExtension.CAROUSEL_DEFAULT_TITLE;
export const LIST_DEFAULT_TITLE = DynamicReviewsExtension.LIST_DEFAULT_TITLE;

export const USAGE_TOKEN_PREFIX = 'dynamic_reviews_';
export const USAGE_TOKEN_RANDOM_LENGTH = 12;
export const DEFAULT_INTERNAL_NAME = 'Dynamic Reviews';
export const DEFAULT_INTERNAL_DESCRIPTION = 'Display product reviews at checkout';
export const DEFAULT_REVIEWS_TO_DISPLAY = DynamicReviewsExtension.DEFAULT_REVIEWS_TO_DISPLAY;
export const DEFAULT_REVIEWS_PER_PAGE = DynamicReviewsExtension.DEFAULT_REVIEWS_PER_PAGE;
export const DEFAULT_MIN_NUMBER_OF_REVIEWS = ProductReviews.DEFAULT_MIN_NUMBER_OF_REVIEWS;

export interface ExcludedProductItem extends ProductReviews.ExcludedProduct {
  name: string;
  description?: string;
  imageUrl?: string;
  sku?: string;
  price?: string;
}

export enum DynamicReviewsTab {
  SETTINGS = 'settings',
  APPEARANCE = 'appearance',
  DISPLAY_CONDITIONS = 'display_conditions',
}

export interface FallbackSettingsFormGroup {
  enabled: FormControl<boolean>;
  text: FormControl<string>;
}

export interface DesignOptionsFormGroup {
  starColor: FormControl<string>;
  backgroundColor: FormControl<string>;
  border: FormControl<CheckoutExtensionUI.BorderType>;
  borderWidth: FormControl<CheckoutExtensionUI.BorderWidth>;
  cornerRadius: FormControl<CheckoutExtensionUI.CornerRadius>;
  padding: FormControl<CheckoutExtensionUI.Padding>;
}

export interface RatingBadgeConfigFormGroup {
  type: FormControl<DynamicReviewsExtension.RatingBadgeType>;
  starColor: FormControl<string>;
}

export interface CarouselConfigFormGroup {
  navigationStyle: FormControl<DynamicReviewsExtension.CarouselNavigationStyle>;
  starPosition: FormControl<DynamicReviewsExtension.StarPosition>;
  title: FormControl<string>;
  description: FormControl<string>;
  design: FormGroup<DesignOptionsFormGroup>;
}

export interface ListConfigFormGroup {
  title: FormControl<string>;
  description: FormControl<string>;
  design: FormGroup<DesignOptionsFormGroup>;
  reviewsPerPage: FormControl<number>;
}

export type ImportMethod = 'csv' | 'integration';

export interface DynamicReviewsFormGroup {
  usageToken: FormControl<string>;
  internalName: FormControl<string>;
  internalDescription: FormControl<string>;
  importMethod: FormControl<ImportMethod>;
  reviewDataSource: FormControl<DynamicReviewsExtension.ReviewDataSource>;
  csvFileUrl: FormControl<string>;
  csvFileName: FormControl<string>;
  csvFileSize: FormControl<number>;
  reviewCount: FormControl<number>;
  pendingCsvContent: FormControl<string>;
  fallbackSettings: FormGroup<FallbackSettingsFormGroup>;
  minStarRatingEnabled: FormControl<boolean>;
  minStarRating: FormControl<number>;
  reviewsToDisplay: FormControl<number>;
  layoutStyle: FormControl<DynamicReviewsExtension.LayoutStyle>;
  ratingBadgeConfig: FormGroup<RatingBadgeConfigFormGroup>;
  carouselConfig: FormGroup<CarouselConfigFormGroup>;
  listConfig: FormGroup<ListConfigFormGroup>;
  displayRules: FormArray<FormGroup<TypedRuleGroup>>;
  minNumberOfReviewsEnabled: FormControl<boolean>;
  minNumberOfReviews: FormControl<number>;
  excludeProductsEnabled: FormControl<boolean>;
  excludeProductsMode: FormControl<ProductReviews.ExclusionMode>;
  excludeProducts: FormControl<ExcludedProductItem[]>;
  excludeProductsRules: FormArray<FormGroup<TypedRuleGroup>>;
}

function isMissingReviewText(content: string): boolean {
  return !content || !content.includes('{review_text}');
}

export function dynamicReviewsContentValidator(group: AbstractControl): ValidationErrors | null {
  const layoutStyle = group.get('layoutStyle')?.value;
  const importMethod = group.get('importMethod')?.value;
  const reviewDataSource = group.get('reviewDataSource')?.value;

  if (importMethod === 'integration' && reviewDataSource === DynamicReviewsExtension.ReviewDataSource.CSV) {
    return { noIntegrationSelected: true };
  }

  if (layoutStyle === DynamicReviewsExtension.LayoutStyle.REVIEW_CAROUSEL) {
    const content = group.get('carouselConfig.description')?.value;
    if (isMissingReviewText(content)) {
      return { missingCarouselReviewText: true };
    }
  }

  if (layoutStyle === DynamicReviewsExtension.LayoutStyle.REVIEW_LIST) {
    const description = group.get('listConfig.description')?.value;
    if (isMissingReviewText(description)) {
      return { missingListReviewText: true };
    }
  }

  return null;
}
